import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { contextapi } from "../../helpers/contextapi";
import { axiosget, axiospost, axiosput } from "../../helpers/httprequest";
import { APIKEY, MerchantId } from "../../components/Keys";
import { appHost } from "../../helpers/uri";
import is_money from "../../helpers/is_money";

import CryptoJS from "crypto-js";
import Swal from "sweetalert2";
import ReactModal from "react-modal";
import "./ActionPayment.css";

const PaymentModes = {
  REMITA: "remita",
  WALLET: "wallet",
};

const ActionPayment = ({ v }) => {
  const queryClient = useQueryClient();
  const context = React.useContext(contextapi);
  const [loading, setLoading] = React.useState({
    verify: false,
    invoice: false,
    wallet: false,
  });
  const [showModal, setShowModal] = React.useState(false);
  const [paymentMode, setPaymentMode] = React.useState(PaymentModes.REMITA);
  const { data: wallet, isLoading: walletLoading } = useQuery({
    queryKey: ["myWalletDataset"],
    queryFn: async () => {
      const req = await axiosget(`ms_student_wallet_transactions`);
      return req?.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const user = context?.values?.student;
  let walletBalance = 0;
  walletBalance = !walletLoading ? wallet.wallet?.amount : 0;
  const totalAmount = v?.feeItems?.scholar?.percentage
    ? (v?.feeItems?.items.reduce((a, b) => a + b?.amount, 0) / 100) * v?.feeItems?.scholar?.percentage
    : v?.feeItems?.items.reduce((a, b) => a + b?.amount, 0);

  const generateRemitaInvoice = async () => {
    try {
      setLoading((prev) => ({ ...prev, invoice: true }));

      const serviceTypeId = v?.feeItems?.fee?.serviceID;
      const orderId = new Date().getTime();
      const apiHash = CryptoJS.SHA512(MerchantId + serviceTypeId + orderId + totalAmount + APIKEY);

      const paymentData = {
        serviceTypeId,
        amount: totalAmount,
        orderId,
        payerName: `${user.firstname} ${user.middlename} ${user.lastname}`,
        payerEmail: user?.emailaddress,
        payerPhone: user?.phonenumber,
        description: `Tuition Fee Payment ${user.StudentID} in ${user.level} ${user.semester}`,
      };

      const response = await fetch("https://login.remita.net/remita/exapp/api/v1/send/api/echannelsvc/merchant/api/paymentinit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `remitaConsumerKey=${MerchantId},remitaConsumerToken=${apiHash}`,
        },
        body: JSON.stringify(paymentData),
      });

      const responseText = await response.text();
      const responseData = JSON.parse(responseText.substring(responseText.indexOf("{"), responseText.lastIndexOf("}") + 1));

      if (!responseData?.RRR) {
        throw new Error(responseData.statusMessage || "Failed to generate RRR");
      }

      const saveResponse = await axiospost("ms_transactions", {
        level: v.level,
        semester: v.semester,
        list: v?.feeItems?.items,
        amount: totalAmount,
        serviceId: serviceTypeId,
        rrr: responseData.RRR,
        data: paymentData,
      });

      if (saveResponse?.status !== 200 || saveResponse?.data?.type === "error") {
        throw new Error(saveResponse?.data?.message || "Failed to store payment reference");
      }

      await Swal.fire({
        icon: "success",
        title: "Payment Reference Generated",
        html: `
          <p>Your transaction RRR has been generated</p>
          <h3 class="my-3">${responseData.RRR}</h3>
          <p>Please copy and keep it safe</p>
        `,
      });

      queryClient.invalidateQueries({ queryKey: ["myTuitionHistory"] });
      setShowModal(false);
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading((prev) => ({ ...prev, invoice: false }));
    }
  };

  const verifyPayment = async () => {
    try {
      setLoading((prev) => ({ ...prev, verify: true }));

      const rrr = v?.attempted?.rrr;
      const apiHash = CryptoJS.SHA512(rrr + APIKEY + MerchantId);

      const response = await fetch(`https://login.remita.net/remita/exapp/api/v1/send/api/echannelsvc/${MerchantId}/${rrr}/${apiHash}/status.reg`);

      if (!response.ok) {
        throw new Error("Verification failed due to network error");
      }

      const responseData = await response.json();

      if (responseData?.status !== "00") {
        throw new Error(responseData?.message || "Payment verification failed");
      }

      const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(responseData), "keyIstheKeywith01").toString();

      const updateResponse = await axiosput("ms_transactions", {
        data: encryptedData,
      });

      if (updateResponse?.status !== 200) {
        throw new Error("Failed to update payment status");
      }

      Swal.fire(
        updateResponse?.data?.type === "success" ? "Success" : "Info",
        updateResponse?.data?.message,
        updateResponse?.data?.type === "success" ? "success" : "info"
      );

      if (updateResponse?.data?.type === "success") {
        queryClient.invalidateQueries({ queryKey: ["myTuitionHistory"] });
      }
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading((prev) => ({ ...prev, verify: false }));
    }
  };

  const payWithWallet = async () => {
    try {
      setLoading((prev) => ({ ...prev, wallet: true }));

      if (walletBalance < totalAmount) {
        throw new Error("Insufficient wallet balance");
      }

      const paymentResponse = await axiospost("ms_transactions/wallet", {
        level: v.level,
        semester: v.semester,
        items: v?.feeItems?.items,
        amount: totalAmount,
        serviceId: v?.feeItems?.fee?.serviceID,
      });

      if (paymentResponse?.status !== 200 || paymentResponse?.data?.type === "error") {
        throw new Error(paymentResponse?.data?.message || "Payment processing failed");
      }

      await Swal.fire({
        icon: "success",
        title: "Payment Successful",
        text: paymentResponse?.data?.message,
      });

      queryClient.invalidateQueries({ queryKey: ["myTuitionHistory"] });
      setShowModal(false);
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading((prev) => ({ ...prev, wallet: false }));
    }
  };

  const openReceipt = () => {
    window.open(`${appHost}payment-receipt/${v?.payment?._id}`, "_blank", "width=450,height=650");
  };

  const renderPaymentButton = () => {
    if (!v?.isAdmitted) {
      return (
        <button className="btn btn-secondary" disabled>
          Not Admitted
        </button>
      );
    }

    if (v?.payment) {
      return (
        <button className="btn btn-success" onClick={openReceipt}>
          Print Receipt
        </button>
      );
    }

    if (v?.attempted) {
      return (
        <button className="btn btn-warning" onClick={verifyPayment} disabled={loading.verify}>
          {loading.verify ? "Verifying..." : `Verify Payment (${v?.attempted?.rrr})`}
        </button>
      );
    }

    if (v?.isActive) {
      return (
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          Pay Tuition
        </button>
      );
    }

    return (
      <button className="btn btn-secondary" disabled>
        Payment Closed
      </button>
    );
  };

  return (
    <>
      <div className="payment-action-container">{renderPaymentButton()}</div>

      <ReactModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        className="luxury-modal"
        overlayClassName="luxury-modal-overlay"
      >
        <div className="modal-content luxury-card">
          <div className="modal-header luxury-header">
            <h5 className="modal-title">
              <i className="fas fa-gem mr-2"></i>
              Payment Invoice
            </h5>
            <button type="button" className="luxury-close" onClick={() => setShowModal(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="modal-body luxury-body">
            <div className="payment-summary luxury-summary">
              <div className="summary-header">
                <i className="fas fa-receipt mr-2"></i>
                <h6>Payment Summary</h6>
              </div>

              <div className="luxury-table-container">
                <table className="luxury-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v?.feeItems?.items?.map((item, index) => (
                      <tr key={item._id} className="luxury-table-row">
                        <td>{index + 1}</td>
                        <td>{item.label}</td>
                        <td className="text-right">{is_money(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {v?.feeItems?.scholar && (
                      <>
                        <tr className="subtotal-row">
                          <td colSpan="2">Subtotal</td>
                          <td className="text-right">{is_money(v?.feeItems?.items.reduce((a, b) => a + b?.amount, 0))}</td>
                        </tr>
                        <tr className="discount-row">
                          <td colSpan="2">Scholarship ({v?.feeItems?.scholar?.percentage}%)</td>
                          <td className="text-right">-{is_money(v?.feeItems?.items.reduce((a, b) => a + b?.amount, 0) - totalAmount)}</td>
                        </tr>
                      </>
                    )}
                    <tr className="total-row">
                      <td colSpan="2">Total Amount</td>
                      <td className="text-right">{is_money(totalAmount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="payment-modes luxury-modes">
              <div className="luxury-mode-toggle">
                <button
                  className={`mode-btn ${paymentMode === PaymentModes.REMITA ? "active-mode" : ""}`}
                  onClick={() => setPaymentMode(PaymentModes.REMITA)}
                >
                  <i className="fas fa-credit-card mr-2"></i>
                  Pay via Remita
                </button>
                <button
                  className={`mode-btn ${paymentMode === PaymentModes.WALLET ? "active-mode" : ""}`}
                  onClick={() => setPaymentMode(PaymentModes.WALLET)}
                >
                  <i className="fas fa-wallet mr-2"></i>
                  Wallet: {is_money(walletBalance)}
                </button>
              </div>

              <div className="luxury-payment-action">
                {paymentMode === PaymentModes.REMITA ? (
                  <button className="luxury-btn remita-btn" onClick={generateRemitaInvoice} disabled={loading.invoice}>
                    {loading.invoice ? <span className="spinner-border spinner-border-sm mr-2"></span> : <i className="fas fa-lock mr-2"></i>}
                    Pay {is_money(totalAmount)} via Remita
                  </button>
                ) : (
                  <button
                    className={`luxury-btn ${walletBalance >= totalAmount ? "wallet-success" : "wallet-danger"}`}
                    onClick={payWithWallet}
                    disabled={loading.wallet || walletBalance < totalAmount}
                  >
                    {loading.wallet ? (
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                    ) : (
                      <i className={`fas ${walletBalance >= totalAmount ? "fa-check-circle" : "fa-exclamation-circle"} mr-2`}></i>
                    )}
                    {walletBalance >= totalAmount ? `Pay ${is_money(totalAmount)}` : `Need ${is_money(totalAmount - walletBalance)} more`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default ActionPayment;
